import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useRepositoryPolling } from '../../../hooks/useRepositoryPolling';
import { DatabaseIcon, PlusIcon } from '../../../icons/SvgIcons';
import {
  authorizeRepository,
  connectRepository,
  fetchRepositories,
  fetchBranchesByAiSystem
} from '../../../services/RepositoryService';
import {
  appendRepositories,
  connectRepositoryFailure,
  connectRepositoryStart,
  connectRepositorySuccess,
  fetchRepositoriesFailure,
  fetchRepositoriesStart,
  fetchRepositoriesSuccess,
  isReqListStepStarted,
  resetFilters,
  setBranchFilter,
  setLastSyncedFilter,
  setProviderFilter,
  setSearchFilter,
  setShowPendingOnly,
  setSorting,
  setStatusFilter
} from '../../../store/slices/repositorySlice';
import { PrimaryButton, WhiteButton } from '../../ui/Button/Buttons';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import { Input } from '../../ui/Input/Input';
import Loader from '../../ui/Loader/Loader';
import Modal from '../../ui/Modal/Modal';
import { SectionHeaderWithAction } from '../../ui/SectionHeaderWithAction/SectionHeaderWithAction';
import { Toast } from '../../ui/Toast/Toast';
import { SearchSection } from '../SearchSection/SearchSection';
import { RepositoryManagementFilters } from './RepositoryManagementFilters';
import { RepositoryManagementTable } from './RepositoryManagementTable';
import { setFromRequirement } from '../../../store/slices/requirementStepsSlice';
import { showToast as showGlobalToast } from '../../../store/slices/toastSlice';
import { useAuthorization } from '../../../hooks/useAuthorization';

export function RepositoryManagement() {
  const firstRender = useRef(true);
  const { id: systemId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { can, roleResolved } = useAuthorization();
  const canManageRepos = roleResolved && can('aiSystems.repository.manage');
  const {
    repositories,
    total,
    loading,
    connectLoading,
    filters,
    pagination,
    sorting
  } = useAppSelector((state) => state.repository);

  const hasAnyRepositories = total > 0;

  const fromRequirement = useAppSelector(
    (state) => state.requirementSteps.fromRequirement
  );
  const requirementSteps = useAppSelector(
    (state) => state.requirementSteps.steps
  );

  useEffect(() => {
    if (fromRequirement) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [fromRequirement]);

  const [showModal, setShowModal] = useState(false);
  const [repositoryUrl, setRepositoryUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('error');
  const [availableBranches, setAvailableBranches] = useState<string[]>([]);
  const [hasCompletedFirstFetch, setHasCompletedFirstFetch] = useState(false);

  // Smart polling - only polls when repositories are syncing
  useRepositoryPolling({
    systemId,
    pollingInterval: 3000, // Poll every 3 seconds
    onSyncComplete: (repository) => {
      if (repository.status === 'ACTIVE') {
        setToastMessage(
          `Repository "${repository.name}" sync completed successfully`
        );
        setToastType('success');
        setShowToast(true);
        return;
      }

      setShowToast(false);
      dispatch(
        showGlobalToast({
          message: 'Repository sync failed.',
          type: 'error'
        })
      );
    }
  });

  const hasMore = repositories.length < total;
  const isAnnexIVStarted = isReqListStepStarted(requirementSteps, 'ANNEX_IV');
  const isFriaStarted = isReqListStepStarted(requirementSteps, 'FRIA_REQUIRED');
  const isDpiaStarted = isReqListStepStarted(requirementSteps, 'DPIA_REQUIRED');

  const disableConnectButton =
    isAnnexIVStarted || isFriaStarted || isDpiaStarted || !canManageRepos;

  const hasActiveFilters = useMemo(() => {
    return (
      (filters.search && filters.search.length > 0) ||
      (filters.provider && filters.provider.length > 0) ||
      (filters.branch && filters.branch.length > 0) ||
      (filters.status && filters.status.length > 0) ||
      (filters.lastSyncedFrom !== null && filters.lastSyncedTo !== null) ||
      filters.showPendingOnly
    );
  }, [filters]);

  const handleFetchRepositories = useCallback(async () => {
    if (!systemId) return;

    dispatch(fetchRepositoriesStart());
    try {
      // If showPendingOnly is enabled, force status filter to PENDING_AUTH
      const effectiveStatus = filters.showPendingOnly
        ? ['PENDING_AUTH' as const]
        : filters.status.length > 0
          ? filters.status
          : undefined;

      const response = await fetchRepositories({
        aiSystemId: systemId,
        offset: 0,
        limit: 10,
        search: filters.search || undefined,
        provider: filters.provider.length > 0 ? filters.provider : undefined,
        branch: filters.branch.length > 0 ? filters.branch : undefined,
        status: effectiveStatus,
        lastSyncedFrom: filters.lastSyncedFrom || undefined,
        lastSyncedTo: filters.lastSyncedTo || undefined,
        sortBy: sorting.sortBy,
        order: sorting.sortOrder
      });
      dispatch(fetchRepositoriesSuccess(response));
    } catch (error: any) {
      dispatch(fetchRepositoriesFailure(error.message));
      setToastMessage(error.message || 'Failed to fetch repositories');
      setShowToast(true);
    } finally {
      if (firstRender.current) {
        firstRender.current = false;
        setHasCompletedFirstFetch(true);
      }
    }
  }, [systemId, filters, sorting, dispatch]);

  useEffect(() => {
    firstRender.current = true;
    setHasCompletedFirstFetch(false);
  }, [systemId]);

  useEffect(() => {
    if (systemId) {
      handleFetchRepositories();
    }
  }, [systemId, handleFetchRepositories]);

  useEffect(() => {
    const loadBranches = async () => {
      if (!systemId) return;

      try {
        const branches = await fetchBranchesByAiSystem(systemId);
        setAvailableBranches(branches);
      } catch (error: any) {
        console.error('Failed to fetch branches:', error);
        // Silently fail, branches filter will just be empty
      }
    };

    loadBranches();
  }, [systemId]);

  const validateRepositoryUrl = (url: string): boolean => {
    // Check if URL is empty
    if (!url || url.trim() === '') {
      return false;
    }

    // We no longer restrict to specific git providers (GitHub, GitLab,
    // Bitbucket) — just check that it's a well-formed http(s) URL.
    try {
      const parsed = new URL(url.trim());
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleRepositoryUrlChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newUrl = e.target.value;
    setRepositoryUrl(newUrl);

    // Validate in real-time and show error only if user has typed something
    if (newUrl.trim() !== '') {
      setUrlError(
        validateRepositoryUrl(newUrl) ? '' : 'Invalid URL format'
      );
    } else {
      setUrlError('');
    }
  };

  const handleConnectRepository = async () => {
    if (!canManageRepos) {
      return;
    }

    if (!systemId || !repositoryUrl || !validateRepositoryUrl(repositoryUrl)) {
      console.log('Missing data or invalid URL:', { systemId, repositoryUrl });
      return;
    }

    console.log('Connecting repository:', {
      aiSystemId: systemId,
      url: repositoryUrl
    });
    dispatch(connectRepositoryStart());
    try {
      const response = await connectRepository(systemId, {
        url: repositoryUrl
      });
      console.log('Repository connected successfully:', response);
      dispatch(connectRepositorySuccess(response));
      dispatch(setFromRequirement(false));
      setShowModal(false);
      setRepositoryUrl('');
      setUrlError('');

      // Trigger GitHub authorization after successful connection
      if (response.id && response.provider) {
        authorizeRepository(response.id, response.provider);
      }
    } catch (error: any) {
      console.error('Failed to connect repository:', error);
      dispatch(connectRepositoryFailure(error.message));
      setToastMessage(error.message || 'Failed to connect repository');
      setShowToast(true);
    }
  };

  const handleLoadMore = useCallback(async () => {
    if (!systemId || loading || !hasMore) return;

    dispatch(fetchRepositoriesStart());
    try {
      // If showPendingOnly is enabled, force status filter to PENDING_AUTH
      const effectiveStatus = filters.showPendingOnly
        ? ['PENDING_AUTH' as const]
        : filters.status.length > 0
          ? filters.status
          : undefined;

      const response = await fetchRepositories({
        aiSystemId: systemId,
        offset: repositories.length,
        limit: 10,
        search: filters.search || undefined,
        provider: filters.provider.length > 0 ? filters.provider : undefined,
        branch: filters.branch.length > 0 ? filters.branch : undefined,
        status: effectiveStatus,
        lastSyncedFrom: filters.lastSyncedFrom || undefined,
        lastSyncedTo: filters.lastSyncedTo || undefined,
        sortBy: sorting.sortBy,
        order: sorting.sortOrder
      });
      dispatch(appendRepositories(response));
    } catch (error: any) {
      dispatch(fetchRepositoriesFailure(error.message));
      setToastMessage(error.message || 'Failed to load more repositories');
      setShowToast(true);
    }
  }, [
    systemId,
    loading,
    hasMore,
    repositories.length,
    filters,
    sorting,
    dispatch
  ]);

  const handleSort = (sortKey: string) => {
    if (sorting.sortBy === sortKey) {
      dispatch(
        setSorting({
          sortBy: sortKey,
          sortOrder: sorting.sortOrder === 'asc' ? 'desc' : 'asc'
        })
      );
    } else {
      dispatch(setSorting({ sortBy: sortKey, sortOrder: 'asc' }));
    }
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setRepositoryUrl('');
    setUrlError('');
    dispatch(setFromRequirement(false));
  };

  const handleError = (message: string) => {
    setToastMessage(message);
    setToastType('error');
    setShowToast(true);
  };

  const handleSuccess = (message: string) => {
    // console.log('handleSuccess called with message:', message);
    setToastMessage(message);
    setToastType('success');
    setShowToast(true);
  };

  // Get branch options from API
  const branchOptions = useMemo(() => {
    return availableBranches.map((branch) => ({
      value: branch,
      label: branch
    }));
  }, [availableBranches]);

  if (!hasCompletedFirstFetch) {
    return (
      <div className="absolute inset-0 top-48 left-[100px] 2xl:left-64 z-10 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (
    !loading &&
    repositories.length === 0 &&
    !hasActiveFilters &&
    !hasAnyRepositories
  ) {
    return (
      <>
        <div className="flex flex-col gap-6 flex-1 h-[calc(100%_-_75px)]">
          <EmptyState
            title="No Repositories yet"
            description="Connect your first repository to begin compliance process."
            icon={DatabaseIcon()}
            buttonText="Connect to Repository"
            buttonIcon={PlusIcon()}
            onButtonClick={() => {
              if (!canManageRepos) {
                return;
              }
              setShowModal(true);
            }}
            classes="border-0 shadow-none flex-1"
          />
        </div>

        <Modal
          open={showModal}
          setOpen={(open) => {
            if (!open) handleCloseModal();
          }}
          dialogPanelClassnames="w-[820px]"
        >
          <div className="flex flex-col gap-6">
            <h2 className="text-gray-900 text-lg leading-6 font-medium">
              Connect Repository
            </h2>
            <Input
              id="repoUrl"
              label="Repository URL"
              maxLength={256}
              onChange={handleRepositoryUrlChange}
              value={repositoryUrl}
              placeholder="Enter URL"
              labelClassName="label"
              inputClassName="input"
              error={urlError}
            />
            <div className="flex gap-3 self-end">
              <WhiteButton onClick={handleCloseModal}>Cancel</WhiteButton>
              <PrimaryButton
                disabled={
                  !validateRepositoryUrl(repositoryUrl) || connectLoading
                }
                onClick={handleConnectRepository}
              >
                {connectLoading ? 'Connecting...' : 'Connect Repository'}
              </PrimaryButton>
            </div>
          </div>
        </Modal>

        {showToast && (
          <Toast
            showToast={showToast}
            setShowToast={setShowToast}
            text={toastMessage}
            type={toastType}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6 flex-1 h-[calc(100%_-_50px)]">
        <SectionHeaderWithAction
          text="Connected Repositories"
          button={
            <PrimaryButton
              icon={PlusIcon()}
              onClick={() => {
                if (!canManageRepos) {
                  return;
                }
                setShowModal(true);
              }}
              size="xs"
              disabled={disableConnectButton}
            >
              Connect to Repository
            </PrimaryButton>
          }
        />

        <SearchSection
          searchValue={filters.search}
          onSearchChange={(value) => dispatch(setSearchFilter(value))}
          placeholder="Search by keyword"
        />
        <RepositoryManagementFilters
          selectedProvider={filters.provider}
          onProviderChange={(value) => dispatch(setProviderFilter(value))}
          selectedBranch={filters.branch}
          onBranchChange={(value) => dispatch(setBranchFilter(value))}
          selectedStatus={filters.status}
          onStatusChange={(value) => dispatch(setStatusFilter(value))}
          lastSyncedFrom={filters.lastSyncedFrom}
          lastSyncedTo={filters.lastSyncedTo}
          onLastSyncedChange={(from, to) =>
            dispatch(setLastSyncedFilter({ from, to }))
          }
          showPendingOnly={filters.showPendingOnly}
          onShowPendingOnlyChange={(value) =>
            dispatch(setShowPendingOnly(value))
          }
          onResetFilters={handleResetFilters}
          branchOptions={branchOptions}
        />
        <div className="flex-1 w-full relative">
          {loading ? (
            <div className="absolute inset-0 top-24 bg-white bg-opacity-50 z-10 flex items-center justify-center">
              <Loader />
            </div>
          ) : (
            <RepositoryManagementTable
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              sortBy={sorting.sortBy}
              sortOrder={sorting.sortOrder}
              onSort={handleSort}
              onError={handleError}
              onSuccess={handleSuccess}
              canManageRepositories={canManageRepos}
            />
          )}
        </div>
      </div>

      <Modal
        open={showModal}
        setOpen={(open) => {
          if (!open) handleCloseModal();
        }}
        dialogPanelClassnames="w-[820px]"
      >
        <div className="flex flex-col gap-6">
          <h2 className="text-gray-900 text-lg leading-6 font-medium">
            Connect Repository
          </h2>
          <Input
            id="repoUrl"
            label="Repository URL"
            maxLength={256}
            onChange={handleRepositoryUrlChange}
            value={repositoryUrl}
            placeholder="Enter URL"
            labelClassName="label"
            inputClassName="input"
            error={urlError}
          />
          <div className="flex gap-3 self-end">
            <WhiteButton onClick={handleCloseModal}>Cancel</WhiteButton>
            <PrimaryButton
              disabled={!validateRepositoryUrl(repositoryUrl) || connectLoading}
              onClick={handleConnectRepository}
            >
              {connectLoading ? 'Connecting...' : 'Connect Repository'}
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      {showToast && toastType !== 'error' && (
        <Toast
          showToast={showToast}
          setShowToast={setShowToast}
          text={toastMessage}
          type={toastType}
        />
      )}
    </>
  );
}
